/**
 * NotificationCenter — in-app bell + dropdown listing persistent notifications.
 *
 * Reads the same `/notifications` feed the OS-push poller uses (shared react-query
 * cache key), so this is the in-app place to READ notifications in full, mark them
 * read, dismiss them, and navigate to the related item. Backend endpoints already
 * exist: GET /notifications, POST /notifications/{id}/ack, /dismiss, /clear.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as notificationsAPI from '@/api/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { getNotificationLabel, getNotificationUrl } from '@/lib/notifications';
import type { PendingNotification } from '@/types/api';

const POLL_INTERVAL = 60000;

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem('notifications-muted') === 'true');
  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem('notifications-muted', String(next));
      return next;
    });
  };
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => notificationsAPI.getAll(),
    refetchInterval: POLL_INTERVAL,
    refetchIntervalInBackground: true,
  });

  const notifications: PendingNotification[] = data?.notifications ?? [];
  const unread = notifications.filter((n) => n.status !== 'acknowledged').length;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => notificationsAPI.dismiss(id),
    onSuccess: invalidate,
  });
  const clearMutation = useMutation({
    mutationFn: () => notificationsAPI.clearAll(),
    onSuccess: invalidate,
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleOpenItem = (n: PendingNotification) => {
    // Navigate only — do NOT acknowledge/remove. The user dismisses via ✕.
    const url = getNotificationUrl(n);
    if (url) {
      window.location.hash = url;
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-expanded={open}
        className="relative rounded-lg p-2.5 text-text-secondary transition-colors hover:bg-surface-container-high hover:text-text-primary"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-12 z-50 w-[min(22rem,calc(100vw-1rem))] overflow-hidden rounded-xl border border-outline-variant/30 bg-bg-surface shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-outline-variant/20 px-4 py-2.5">
            <span className="text-sm font-semibold text-text-primary">Notifications</span>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMute}
                title={muted ? 'Pop-up notifications are off — click to turn on' : 'Turn off pop-up notifications'}
                className={`text-xs font-medium transition-colors ${muted ? 'text-primary' : 'text-text-secondary hover:text-text-primary'}`}
              >
                {muted ? 'Muted' : 'Mute'}
              </button>
              {notifications.length > 0 && (
                <button
                  onClick={() => clearMutation.mutate()}
                  className="text-xs font-medium text-text-secondary transition-colors hover:text-text-primary"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-text-secondary">
                You're all caught up.
              </div>
            ) : (
              <ul className="divide-y divide-outline-variant/15">
                {notifications.map((n) => {
                  const actionable = !!getNotificationUrl(n);
                  return (
                    <li
                      key={n.id}
                      className={`group flex gap-3 px-4 py-3 transition-colors hover:bg-surface-container-high ${
                        n.status !== 'acknowledged' ? 'bg-primary/5' : ''
                      }`}
                    >
                      <button
                        onClick={() => handleOpenItem(n)}
                        className={`min-w-0 flex-1 text-left ${actionable ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="truncate text-xs font-semibold uppercase tracking-wide text-primary">
                            {getNotificationLabel(n.kind)}
                          </span>
                          <span className="shrink-0 text-[11px] text-text-secondary">
                            {timeAgo(n.created_at)}
                          </span>
                        </div>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-text-primary">
                          {n.text || n.body || n.title}
                        </p>
                      </button>
                      <button
                        onClick={() => n.id && dismissMutation.mutate(n.id)}
                        aria-label="Dismiss notification"
                        className="h-fit rounded-md p-1 text-text-secondary opacity-0 transition-opacity hover:bg-surface-container-highest hover:text-text-primary focus:opacity-100 group-hover:opacity-100"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
